import styled from "@emotion/styled";
import type { StepColumnType } from "@stepfinance/react";
import {
  CellDefault,
  formatBalance,
  formatPercentage,
  StepAssetBreakdownPopoverChip,
  StepPopoverCellWrapper,
  TagInfo,
  TagInfoContainer,
  transparentWhite,
  white,
} from "@stepfinance/react";
import { Tooltip } from "antd";

import { ReactComponent as InfoIcon } from "../../../../assets/info-round.svg";
import type { ReformattedCollateralizedPosition } from "../../../../types/lending";
import type { ColumnMapType, ExtendedStepColumnsType, GetColumnsArgs } from "../../../../utils/columns";
import { HUBBLE_STSOL_TOOLTIP_TEXT } from "../../../../utils/lending/hubble";
import { getTokenBySymbol } from "../../../../utils/tokenInfo";
import { formatStringWithPlaceholders } from "../../../../utils/utils";
import { IconTextContainer, TokenIconText } from "../../../shared";
import CellValue from "../../../Table/CellValue";
import { TokenIcon } from "../../../TokenIcon";
import { LendingBalanceCell, NetPositionCell } from "./shared";

const StyledPopoverContent = styled.div`
  width: 14em;
`;

const assetColumn = ({ commonText }: GetColumnsArgs["args"]): StepColumnType<ReformattedCollateralizedPosition> => ({
  dataIndex: "asset",
  key: `asset-column`,
  title: commonText.asset,
  render(_, { asset, tokenSymbol }) {
    return (
      <>
        {typeof asset === "string" ? (
          <IconTextContainer>
            <TokenIcon mintAddress={asset} />
            <TokenIconText bold>{tokenSymbol}</TokenIconText>
          </IconTextContainer>
        ) : (
          <IconTextContainer>
            <TokenIcon mintAddress={asset} />
            <TokenIconText bold>
              {tokenSymbol[0]}/{tokenSymbol[1]}
            </TokenIconText>
          </IconTextContainer>
        )}
      </>
    );
  },
});

const depositedColumn = ({
  lendingText,
}: GetColumnsArgs["args"]): StepColumnType<ReformattedCollateralizedPosition> => ({
  dataIndex: "deposited",
  key: `deposited-column`,
  title: lendingText.deposits,
  className: "column-number",
  render(value: number, record) {
    return (
      <LendingBalanceCell>
        {value === 0 ? (
          <CellDefault />
        ) : (
          <>
            <CellValue cellType="number">
              {formatBalance(value, getTokenBySymbol(record.tokenSymbol)?.decimals)}
            </CellValue>
            <TagInfoContainer>
              <TagInfo tagType="performance" performanceNumber={record.depositApy ?? 0}>
                {formatPercentage(record.depositApy ?? 0, { capped: true, performance: true })}
              </TagInfo>
            </TagInfoContainer>
          </>
        )}
      </LendingBalanceCell>
    );
  },
  sorter: (a, b) => {
    return a.deposited - b.deposited;
  },
});

const borrowedColumn = ({
  lendingText,
}: GetColumnsArgs["args"]): StepColumnType<ReformattedCollateralizedPosition> => ({
  dataIndex: "borrowed",
  key: `lending-borrowed`,
  title: lendingText.borrows,
  className: "column-number",
  render(value: number, record) {
    return (
      <LendingBalanceCell>
        {value === 0 ? (
          <CellDefault />
        ) : (
          <>
            <CellValue cellType="number">
              {formatBalance(value, getTokenBySymbol(record.tokenSymbol)?.decimals)}
            </CellValue>
            <TagInfoContainer>
              <TagInfo tagType="performance" performanceNumber={record.borrowApy ?? 0}>
                {formatPercentage(record.borrowApy ?? 0, { capped: true, performance: true })}
              </TagInfo>
            </TagInfoContainer>
          </>
        )}
      </LendingBalanceCell>
    );
  },
  sorter: (a, b) => {
    return a.borrowed - b.borrowed;
  },
});

const NetPositionCellComponent = (props) => {
  const { value, record, children, fontColor = "", extraTagInfo } = props;
  return (
    <NetPositionCell>
      <CellValue fontWeight="bold" cellType="userPositionValue" fontColor={fontColor}>
        {value * record.tokenPrice}
      </CellValue>
      <TagInfoContainer>
        <TagInfo performanceNumber={record.netApy ?? 0} tagType="performance">
          {formatPercentage(record.netApy ?? 0, { performance: true })}
          {extraTagInfo ? extraTagInfo(record.tokenSymbol) : ""}
        </TagInfo>
      </TagInfoContainer>
      {children}
    </NetPositionCell>
  );
};

const netColumn = ({
  totalAPY,
  lendingText,
}: GetColumnsArgs["args"]): StepColumnType<ReformattedCollateralizedPosition> => ({
  dataIndex: "net",
  key: `net-column`,
  title: () => {
    return (
      <LendingBalanceCell>
        {lendingText.netPositionValue}{" "}
        <TagInfoContainer>
          <TagInfo tagType="performance" performanceNumber={totalAPY}>
            {formatPercentage(totalAPY, { performance: true })}
          </TagInfo>
        </TagInfoContainer>
      </LendingBalanceCell>
    );
  },
  className: "column-number",
  render(value, record) {
    // eslint-disable-next-line react/no-children-prop
    return <NetPositionCellComponent children value={value} record={record} />;
  },
  sorter: (a, b) => {
    return a.net - b.net;
  },
  defaultSortOrder: "descend",
});

// custom columns from default
const hiddenBorrowedColumn = ({
  lendingText,
}: GetColumnsArgs["args"]): StepColumnType<ReformattedCollateralizedPosition> => ({
  ...borrowedColumn({ lendingText }),
  className: "hidden-column",
});

// Collateral Vault columns
const hubbleExtraInfo = (tokenSymbol: string) => {
  switch (tokenSymbol) {
    case "stSOL":
      return (
        <Tooltip placement="bottom" title={HUBBLE_STSOL_TOOLTIP_TEXT} arrowPointAtCenter={true}>
          <StyledInfoIcon />
        </Tooltip>
      );
    default:
      return "";
  }
};

const extraTagInfos = {
  Hubble: hubbleExtraInfo,
};

const collateralVaultAssetColumn = ({
  lendingText,
  commonText,
}: GetColumnsArgs["args"]): StepColumnType<ReformattedCollateralizedPosition> => ({
  ...assetColumn({ commonText }),
  title: lendingText.collateralVault.title,
});

const collateralVaultDepositedColumn = ({
  lendingText,
}: GetColumnsArgs["args"]): StepColumnType<ReformattedCollateralizedPosition> => ({
  ...depositedColumn({ lendingText }),
  render(value: number, record) {
    const extraInfo = extraTagInfos[record.platform];
    return (
      <LendingBalanceCell>
        {value === 0 ? (
          <CellDefault />
        ) : (
          <>
            <CellValue cellType="number">
              {formatBalance(value, getTokenBySymbol(record.tokenSymbol)?.decimals)}
            </CellValue>
            <TagInfoContainer>
              <TagInfo tagType="performance" performanceNumber={record.depositApy ?? 0}>
                {formatPercentage(record.depositApy ?? 0, { capped: true, performance: true })}
                {extraInfo && extraInfo(record.tokenSymbol)}
              </TagInfo>
            </TagInfoContainer>
          </>
        )}
      </LendingBalanceCell>
    );
  },
  title: lendingText.collateralVault.deposits,
});

const collateralVaultBorrowedColumn = ({
  lendingText,
}: GetColumnsArgs["args"]): StepColumnType<ReformattedCollateralizedPosition> => ({
  ...borrowedColumn({ lendingText }),
  title: lendingText.collateralVault.borrowed,
});

const collateralVaultNetColumn = ({
  totalAPY,
  lendingText,
}: GetColumnsArgs["args"]): StepColumnType<ReformattedCollateralizedPosition> => ({
  ...netColumn({ totalAPY }),
  title: lendingText.netPositionValue,
  render(value, record) {
    return (
      <NetPositionCellComponent
        value={value}
        record={record}
        fontColor={record.borrowed > 0 ? transparentWhite : ""}
        extraTagInfo={extraTagInfos[record.platform]}
      >
        {record.borrowed > 0 && (
          <StepPopoverCellWrapper>
            <StepAssetBreakdownPopoverChip
              title={lendingText.collateralVault.popoverTitle}
              content={
                <StyledPopoverContent>
                  {formatStringWithPlaceholders(lendingText.collateralVault.popoverDescription, [record.tokenSymbol])}
                </StyledPopoverContent>
              }
              assetValue={1}
            />
          </StepPopoverCellWrapper>
        )}
      </NetPositionCellComponent>
    );
  },
});

const defaultColumns: ExtendedStepColumnsType<ReformattedCollateralizedPosition> = [
  assetColumn,
  depositedColumn,
  borrowedColumn,
  netColumn,
];
const hiddenBorrowLendingColumns: ExtendedStepColumnsType<ReformattedCollateralizedPosition> = [
  assetColumn,
  depositedColumn,
  hiddenBorrowedColumn,
  netColumn,
];
const collateralVaultLendingColumns: ExtendedStepColumnsType<ReformattedCollateralizedPosition> = [
  collateralVaultAssetColumn,
  collateralVaultDepositedColumn,
  collateralVaultBorrowedColumn,
  collateralVaultNetColumn,
];

export const lendingColumnMap: ColumnMapType<ReformattedCollateralizedPosition> = {
  default: defaultColumns,
  Tulip: hiddenBorrowLendingColumns,
  Francium: hiddenBorrowLendingColumns,
  Hubble: collateralVaultLendingColumns,
  Ratio: collateralVaultLendingColumns,
  Hedge: collateralVaultLendingColumns,
};

const StyledInfoIcon = styled(InfoIcon)`
  height: 12px !important;
  margin-left: 0.2em;
  margin-right: -1px !important;

  path {
    fill: ${white};
  }
`;
